






import { useState } from "react";
import { createProjectApi, createDemoProjectApi, getDemoProjectsApi } from "../../app/api";
import { createPortal } from "react-dom";
import { toast } from "react-hot-toast";

import { useNavigate } from "react-router-dom";
import { PlusIcon, TrashIcon, CloudArrowUpIcon, InformationCircleIcon } from "@heroicons/react/24/outline";

// Blank entry template for Service Name + URLs + Content Path
const blankEntry = () => ({
  service: "",
  service_url: "",
  promotion_url: "",
  callback_url: "",
  header_url: "",
  path: "",
  content: "",
  content_path: "",
});

export default function CreateProjectPage() {
  const navigate = useNavigate();
const [errorModal, setErrorModal] = useState("");
  const [form, setForm] = useState({
    client: "",
    country: "",
    operator: "",
    aggregator: "",
    server: "",
    db_ip: "",
    db_name: "",
    flow_video: null,
    // Common service fields (once per project)
    flow: "",
    currency: "",
    first_day_free: 0,
    charging: "",
    // Repeatable: Service Name + URLs + Content Path
    entries: [blankEntry()],
    // Pricing packs
    packs: [{ pack_type: "", amount: "" }],
  });

  const [loading, setLoading] = useState(false);
  const [demoModalOpen, setDemoModalOpen] = useState(false);
  const [demoForm, setDemoForm] = useState({
    project_name: "",
    server: "",
    backend_port: "",
    path: "",
    domain: "",
    url: "",
    description: "",
  });

  const handleDemoChange = (key, value) => {
    setDemoForm(prev => ({ ...prev, [key]: value }));
  };

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Manual Validation
      if (!demoForm.project_name || !demoForm.server || !demoForm.backend_port || !demoForm.path || !demoForm.domain || !demoForm.url) {
        toast.error("Please fill all required fields marked with *");
        setLoading(false);
        return;
      }

      const res = await createDemoProjectApi(demoForm);
      if (res.success) {
        setDemoModalOpen(false);
        setDemoForm({
          project_name: "",
          server: "",
          backend_port: "",
          path: "",
          domain: "",
          url: "",
          description: "",
        });
        toast.success("Demo Project Created Successfully!");
        navigate("/projects/my");
      }
    } catch (err) {
      setErrorModal(err?.message || "Failed to create demo project");
    } finally {
      setLoading(false);
    }
  };

  const handleProjectChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
  };

  // --- Entry handlers (Service Name + URLs + Content Path) ---
  const handleEntryChange = (eIndex, key, value) => {
    const updated = [...form.entries];
    updated[eIndex][key] = value;
    setForm(prev => ({ ...prev, entries: updated }));
  };

  const addEntry = () => {
    setForm(prev => ({ ...prev, entries: [...prev.entries, blankEntry()] }));
  };

  const removeEntry = (eIndex) => {
    if (form.entries.length <= 1) return;
    setForm(prev => ({ ...prev, entries: prev.entries.filter((_, i) => i !== eIndex) }));
  };

  // --- Pack handlers ---
  const handlePackChange = (pIndex, key, value) => {
    const updated = [...form.packs];
    updated[pIndex][key] = value;
    setForm(prev => ({ ...prev, packs: updated }));
  };

  const addPack = () => {
    setForm(prev => ({ ...prev, packs: [...prev.packs, { pack_type: "", amount: "" }] }));
  };

  const removePack = (pIndex) => {
    if (form.packs.length <= 1) return;
    setForm(prev => ({ ...prev, packs: prev.packs.filter((_, i) => i !== pIndex) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);




// ✅ BASIC VALIDATION
if (
  !form.client ||
  !form.country ||
  !form.operator ||
  !form.server ||
  !form.db_ip ||
  !form.db_name ||
  !form.flow ||
  !form.currency ||
  !form.charging
) {
  setErrorModal("Please fill all required project fields.");
  setLoading(false);
  return;
}

// ✅ Validate entries
for (let i = 0; i < form.entries.length; i++) {
  const e = form.entries[i];

  if (
    !e.service ||
    !e.service_url ||
    !e.callback_url ||
    !e.path ||
    !e.content_path
  ) {
    setErrorModal(`Service #${i + 1}: Please fill all required fields.`);
    setLoading(false);
    return;
  }
}

// ✅ Validate packs
for (let i = 0; i < form.packs.length; i++) {
  const p = form.packs[i];

  if (!p.pack_type || !p.amount) {
    setErrorModal(`Pack #${i + 1}: Type and Amount are required.`);
    setLoading(false);
    return;
  }

  // 🔥 FIX YOUR ERROR (decimal issue)
  if (isNaN(p.amount)) {
    setErrorModal(`Pack #${i + 1}: Amount must be a number.`);
    setLoading(false);
    return;
  }
}






    try {
      const fd = new FormData();

      // Project fields
      fd.append("client", form.client);
      fd.append("country", form.country);
      fd.append("operator", form.operator);
      fd.append("aggregator", form.aggregator);
      fd.append("server", form.server);
      fd.append("db_ip", form.db_ip);
      fd.append("db_name", form.db_name);

      if (form.flow_video) {
        fd.append("video", form.flow_video);
      }

      // Flatten entries into services array (each entry gets the shared common fields + packs)
      const services = form.entries.map(entry => ({
        service: entry.service,
        flow: form.flow,
        currency: form.currency,
        first_day_free: form.first_day_free,
        charging: form.charging,
        service_url: entry.service_url,
        promotion_url: entry.promotion_url,
        callback_url: entry.callback_url,
        header_url: entry.header_url,
        path: entry.path,
        content: entry.content,
        content_path: entry.content_path,
        packs: form.packs,
      }));

      fd.append("services", JSON.stringify(services));

      const res = await createProjectApi(fd);

      if (res.success) {
        navigate("/projects/my");
      }
    } 
    
    // catch (err) {
    //   alert(err.message);
    // }

    catch (err) {
      setErrorModal(
        err?.response?.data?.message ||
        "Invalid input. Please check highlighted fields."
      );
    }
    
    finally {
      setLoading(false);
    }
  };



  return (
    <div className="min-h-screen  p-6 md:mt-16 bg-gradient-to-br from-[#140c2b] via-[#1f1147] to-[#0f0825] md:mt-20 rounded-[20px]">
      {demoModalOpen && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="w-full max-w-2xl bg-[#140c2b] border border-white/10 rounded-3xl p-8 shadow-2xl overflow-y-auto max-h-[90vh]">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">Create Demo Project</h2>
              <button onClick={() => setDemoModalOpen(false)} className="text-white/60 hover:text-white text-2xl">&times;</button>
            </div>
            <form onSubmit={handleDemoSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input label="Project Name" required={true} placeholder="e.g. Netflix Clone" value={demoForm.project_name} onChange={v => handleDemoChange("project_name", v)} />
                <Input label="Server" required={true} placeholder="e.g. AWS" value={demoForm.server} onChange={v => handleDemoChange("server", v)} />
                <Input label="Backend Port" required={true} placeholder="e.g. 5000" value={demoForm.backend_port} onChange={v => handleDemoChange("backend_port", v)} />
                <Input label="Path" required={true} placeholder="e.g. /var/www/netflix" value={demoForm.path} onChange={v => handleDemoChange("path", v)} />
                <Input label="Domain" required={true} placeholder="e.g. netflix-demo.com" value={demoForm.domain} onChange={v => handleDemoChange("domain", v)} />
                <Input label="URL" required={true} placeholder="https://netflix-demo.com" value={demoForm.url} onChange={v => handleDemoChange("url", v)} />
              </div>
              <div className="col-span-full">
                <label className="text-sm text-white/60 mb-1 block">Description</label>
                <textarea
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:ring-2 focus:ring-purple-500 outline-none"
                  placeholder="Brief description of the demo project"
                  value={demoForm.description}
                  onChange={e => handleDemoChange("description", e.target.value)}
                />
              </div>
              <div className="flex justify-end gap-4 mt-8">
                <button
                  type="button"
                  onClick={() => setDemoModalOpen(false)}
                  className="px-6 py-2 rounded-xl bg-white/5 text-white hover:bg-white/10 transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-medium shadow-lg hover:scale-[1.02] transition"
                >
                  {loading ? "Creating..." : "Create Demo"}
                </button>
              </div>
            </form>
          </div>
        </div>,
        document.body
      )}
      <div className="max-w-6xl mx-auto">
        {/* Header */}
<div className="flex flex-col md:flex-row md:justify-between md:items-center  mb-6">

<div>
  <h1 className="text-3xl font-semibold text-white whitespace-nowrap">
    Create Project
  </h1>
  <p className="text-white/60 text-sm">
    Configure your service & deployment details
  </p>
</div>

<div className="flex gap-2">
  <button
    type="button"
    onClick={() => setDemoModalOpen(true)}
    className="md:px-5 px-1 py-2 bg-purple-600 text-white rounded-lg shadow hover:bg-purple-700 whitespace-nowrap"
  >
    + Create Demo Project
  </button>
  <button
    type="button"
    onClick={() => navigate("/apps/create")}
    className="md:px-5 px-1 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 whitespace-nowrap"
  >
    + Create App Projects
  </button>
</div>

</div>

        <form onSubmit={handleSubmit} className="space-y-8">

          {/* Project Identity */}
          <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center gap-2 mb-6 text-purple-400">
              <InformationCircleIcon className="w-5 h-5" />
              <h2 className="text-xl font-semibold text-white">Project Identity</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Input label="Client" required placeholder="e.g. Acme Corp" value={form.client} onChange={v => handleProjectChange("client", v)} />
              <Input label="Country" required placeholder="e.g. UAE" value={form.country} onChange={v => handleProjectChange("country", v)} />
              <Input label="Operator" required placeholder="e.g. Etisalat" value={form.operator} onChange={v => handleProjectChange("operator", v)} />
              <Input label="Aggregator" placeholder="e.g. TP" value={form.aggregator} onChange={v => handleProjectChange("aggregator", v)} />

              <Input label="Server IP" required placeholder="e.g. AWS-Production" value={form.server} onChange={v => handleProjectChange("server", v)} />
              <Input label="Database IP" required placeholder="10.0.0.1" value={form.db_ip} onChange={v => handleProjectChange("db_ip", v)} />
              <Input label="Database Name" required placeholder="acme_main" value={form.db_name} onChange={v => handleProjectChange("db_name", v)} />

            </div>







            <div className="mt-6">
              <label className="block text-sm font-medium text-white/40 mb-2 uppercase tracking-wider">Flow Demonstration Video</label>
              <div className="relative group cursor-pointer">
                <input
                  type="file"
                  accept="video/*"
                  onChange={e => handleProjectChange("flow_video", e.target.files[0])}
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                />
                <div className="w-full py-6 px-4 bg-white/5 border-2 border-dashed border-white/10 group-hover:border-purple-500/50 rounded-2xl flex flex-col items-center justify-center transition-all">
                  <CloudArrowUpIcon className="w-8 h-8 text-white/20 group-hover:text-purple-400 transition-colors" />
                  <span className="mt-2 text-white/40 group-hover:text-white/60 transition-colors">
                    {form.flow_video ? form.flow_video.name : "Drop a video file or click to browse"}
                  </span>
                </div>
              </div>
            </div>
          </section>

          {/* Technical Infrastructure */}
          {/* <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl">
            <div className="flex items-center gap-2 mb-6 text-indigo-400">
              <CloudArrowUpIcon className="w-5 h-5" />
              <h2 className="text-xl font-semibold text-white">Technical Infrastructure</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Input label="Server Instance" placeholder="e.g. AWS-Production" value={form.server} onChange={v => handleProjectChange("server", v)} />
              <Input label="Database IP" placeholder="10.0.0.1" value={form.db_ip} onChange={v => handleProjectChange("db_ip", v)} />
              <Input label="Database Name" placeholder="acme_main" value={form.db_name} onChange={v => handleProjectChange("db_name", v)} />
            </div>
            <div className="mt-6">
              <label className="block text-sm font-medium text-white/40 mb-2 uppercase tracking-wider">Flow Demonstration Video</label>
              <div className="relative group cursor-pointer">
                <input
                  type="file"
                  accept="video/*"
                  onChange={e => handleProjectChange("flow_video", e.target.files[0])}
                  className="absolute inset-0 opacity-0 cursor-pointer z-10"
                />
                <div className="w-full py-6 px-4 bg-white/5 border-2 border-dashed border-white/10 group-hover:border-purple-500/50 rounded-2xl flex flex-col items-center justify-center transition-all">
                  <CloudArrowUpIcon className="w-8 h-8 text-white/20 group-hover:text-purple-400 transition-colors" />
                  <span className="mt-2 text-white/40 group-hover:text-white/60 transition-colors">
                    {form.flow_video ? form.flow_video.name : "Drop a video file or click to browse"}
                  </span>
                </div>
              </div>
            </div>
          </section> */}

          {/* Service Configuration */}
          <section className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-2xl ">
            <div className="flex items-center gap-2 mb-6 text-purple-400">
              <InformationCircleIcon className="w-5 h-5 mt-[-10px]" />
              <h2 className="text-xl font-semibold text-white mt-[-10px]">Service Configuration</h2>
            </div>

            {/* Common fields — filled once */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8 mt-[-4px]">
              <Input label="Flow Type" required placeholder="e.g. HE, OTP, SMS" value={form.flow} onChange={v => handleProjectChange("flow", v)} />
              <Input label="Currency" required placeholder="e.g. AED" value={form.currency} onChange={v => handleProjectChange("currency", v)} />
              <Select
                label="First Day Free"
                value={form.first_day_free}
                options={[{ label: "No", value: 0 }, { label: "Yes", value: 1 }]}
                onChange={v => handleProjectChange("first_day_free", Number(v))}
              />
              <Input label="Is Charging?" required placeholder="MO/MT" value={form.charging} onChange={v => handleProjectChange("charging", v)} />
            </div>

            {/* Repeatable: Service Name + URLs + Content Path */}
            <div className="bg-white/5 rounded-2xl p-5 border border-white/5 mb-8 mt-[-18px]">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold uppercase tracking-wider text-white/40 mt-[-14px]">
                  Services &nbsp;
                  <span className="bg-purple-500/20 text-purple-400 text-xs px-2 py-0.5 rounded-full font-normal normal-case ">
                    {form.entries.length}
                  </span>
                </h4>
                <button
                  type="button"
                  onClick={addEntry}
                  className="text-xs font-bold text-indigo-400 mt-[-14px] hover:text-indigo-300 transition-colors flex items-center gap-1"
                >
                  <PlusIcon className="w-3 h-3" /> Add More
                </button>
              </div>

              <div className="space-y-6">
                {form.entries.map((entry, eIndex) => (
                  <div key={eIndex} className="border border-white/5 rounded-xl md:p-4 bg-white/3 relative">
                    {/* Entry index label + remove */}
                    <div className="flex justify-between items-center mb-4">
                      <span className="text-xs mt-[-14px] font-mono text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded-md">
                        #{eIndex + 1}
                      </span>
                      <button
                        type="button"
                        onClick={() => removeEntry(eIndex)}
                        disabled={form.entries.length <= 1}
                        className="p-1.5 mt-[-14px] text-white/10 hover:text-red-400 transition-colors disabled:opacity-30"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>

                    {/* Service Name */}
                    {/* <div className="mb-4">
                      <Input
                        label="Service Name"
                        placeholder="e.g. Movie Club"
                        value={entry.service}
                        onChange={v => handleEntryChange(eIndex, "service", v)}
                      />
                    </div> */}

                    {/* URLs */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <Input
                        label="Service Name" required
                        placeholder="e.g. Movie Club"
                        value={entry.service}
                        onChange={v => handleEntryChange(eIndex, "service", v)}
                      />
                      <Input label="Service URL" required placeholder="https://" value={entry.service_url} onChange={v => handleEntryChange(eIndex, "service_url", v)} />
                      <Input label="Promotion URL" placeholder="https://" value={entry.promotion_url} onChange={v => handleEntryChange(eIndex, "promotion_url", v)} />
                      <Input label="Callback URL" required placeholder="https://" value={entry.callback_url} onChange={v => handleEntryChange(eIndex, "callback_url", v)} />
                      <Input label="Header URL" required placeholder="https://" value={entry.header_url} onChange={v => handleEntryChange(eIndex, "header_url", v)} />

                      <Input label="App Path" required placeholder="/var/www/..." value={entry.path} onChange={v => handleEntryChange(eIndex, "path", v)} />
                      <Input label="Content Store Path" required placeholder="/ext/content/..." value={entry.content_path} onChange={v => handleEntryChange(eIndex, "content_path", v)} />
                    </div>

                    {/* Content Path */}
                    {/* <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Input label="App Path" placeholder="/var/www/..." value={entry.path} onChange={v => handleEntryChange(eIndex, "path", v)} />
                      <Input label="Content ID" placeholder="CID-123" value={entry.content} onChange={v => handleEntryChange(eIndex, "content", v)} />
                      <Input label="Content Store Path" placeholder="/ext/content/..." value={entry.content_path} onChange={v => handleEntryChange(eIndex, "content_path", v)} />
                    </div> */}
                  </div>
                ))}
              </div>
            </div>

            {/* Pricing Packs */}
            <div className="bg-white/5 rounded-2xl p-6 border border-white/5">
              <div className="flex justify-between items-center mb-4">
                <h4 className="text-sm font-bold uppercase tracking-wider text-white/40">Pricing Packs</h4>
                <button
                  type="button"
                  onClick={addPack}
                  className="text-xs font-bold text-indigo-400 hover:text-indigo-300 transition-colors flex items-center gap-1"
                >
                  <PlusIcon className="w-3 h-3" /> Add Price Point
                </button>
              </div>
              <div className="space-y-3">
                {form.packs.map((pack, pIndex) => (
                  <div key={pIndex} className="flex gap-4 items-end animate-in fade-in duration-300">
                    <div className="flex-1">
                      <Input
                        compact
                        label={pIndex === 0 ? "Type" : ""}
                        required
                        placeholder="Daily, Weekly..."
                        value={pack.pack_type}
                        onChange={v => handlePackChange(pIndex, "pack_type", v)}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        compact
                        label={pIndex === 0 ? "Amount" : ""}
                        required
                        placeholder="0.00"
                        value={pack.amount}
                        onChange={v => handlePackChange(pIndex, "amount", v)}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => removePack(pIndex)}
                      className="mb-3 p-2 text-white/10 hover:text-red-400 transition-colors"
                      disabled={form.packs.length <= 1}
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Submit */}
          <div className="pt-6">
            <button
              type="submit"
              disabled={loading}
              className="w-full py-4 bg-gradient-to-r from-purple-600 to-indigo-600  text-white rounded-3xl text-lg font-bold shadow-[0_10px_40px_rgba(124,58,237,0.3)] hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Creating Project..." : "Create Project"}
            </button>
          </div>
        </form>
      </div>

      {/* import { createPortal } from "react-dom"; */}


      {errorModal && createPortal(
  <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
    <div className="bg-[#1f1147] text-white rounded-2xl p-6 w-[90%] max-w-md shadow-xl">

      <h2 className="text-lg font-semibold mb-3 text-red-400">
        ⚠️ Invalid Input
      </h2>

      <p className="text-sm text-white/70 mb-6">
        {errorModal}
      </p>

      <button
        onClick={() => setErrorModal("")}
        className="w-full py-2 bg-red-500 rounded-lg hover:bg-red-600 transition"
      >
        Fix Errors
      </button>
    </div>
  </div>,
    document.body
)}
    </div>
  );
}

function Input({ label, placeholder, value, onChange, type = "text", compact = false, required = false }) {
  return (
    <div className={compact ? "" : "w-full"}>
      {label && (
        <label className="block text-xs font-medium text-white/40 mb-2 uppercase tracking-wider">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        required={required}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-white/20 focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all shadow-inner"
      />
    </div>
  );
}

function Select({ label, value, options, onChange }) {
  return (
    <div className="w-full">
      <label className="block text-xs font-medium text-white/40 mb-2 uppercase tracking-wider">
        {label}
      </label>
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white appearance-none focus:outline-none focus:ring-2 focus:ring-purple-500/50 transition-all shadow-inner"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value} className="bg-[#1f1147] text-white">
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}












// import { useState } from "react";
// import { createProjectApi, uploadDocumentApi } from "../../app/api";
// import { useNavigate } from "react-router-dom";
// import { createPortal } from "react-dom";

// export default function CreateProjectPage() {

//   const [apiError, setApiError] = useState("");

//     const [errors, setErrors] = useState({});
// const [success, setSuccess] = useState(false);
//   const navigate = useNavigate();
//   const user = JSON.parse(localStorage.getItem("user"));

//   const [form, setForm] = useState({
//     client: "",
//     country: "",
//     operator: "",
//     aggregator: "",
//     service: "",
//     flow: "",
//     pricing: "",
//     first_day_free: 0,
//     service_url: "",
//     promotion_url: "",
//     server: "",
//     db_ip: "",
//     db_name: "",
//     path: "",
//     content: "",
//     content_path: "",
//     callback_url: "",
//     header_url: "",
//     charging: "",
//     flow_video: "",
//   });

//   const [loading, setLoading] = useState(false);

//   const handleChange = (key, value) => {
//     setForm((prev) => ({ ...prev, [key]: value }));
//   };

// // const handleSubmit = async (e) => {
// //     e.preventDefault();
  
// //     const validationErrors = validate();
  
// //     if (Object.keys(validationErrors).length > 0) {
// //       setErrors(validationErrors);
// //       return;
// //     }
  
// //     try {
// //       setLoading(true);
// //       setErrors({});
  
// //       let finalFlowVideo = form.flow_video;

// //       // Handle video upload if a file was selected
// //       if (finalFlowVideo instanceof File) {
// //         const fd = new FormData();
// //         fd.append("file", finalFlowVideo);
// //         // Using "OTHER" or any suitable type for documents API
// //         fd.append("document_type", "OTHER");
// //         const docRes = await uploadDocumentApi(fd);
// //         if (docRes?.data?.file_path) {
// //           finalFlowVideo = docRes.data.file_path;
// //         }
// //       }

// //       const payload = {
// //         ...form,
// //         flow_video: finalFlowVideo,
// //         pricing: Number(form.pricing),
// //         first_day_free: Number(form.first_day_free),
// //         created_by: user.id,
// //       };
  
// //       const res = await createProjectApi(payload);
  
// //       if (res.success) {
// //         setSuccess(true); // ✅ show modal
// //       }
  
// //     } 
// //     // catch (err) {
// //     //   alert(err.message);
// //     // } 



// //     catch (err) {
// //       console.log("API ERROR:", err);
    
// //       let message = "Something went wrong";
    
// //       // 🔥 Backend validation errors (like field errors)
// //       if (err?.response?.data?.errors) {
// //         setErrors(err.response.data.errors);
// //         return;
// //       }
    
// //       // 🔥 Backend message
// //       if (err?.response?.data?.message) {
// //         message = err.response.data.message;
// //       }
    
// //       // 🔥 Status-based messages (user friendly)
// //       else if (err?.response?.status === 500) {
// //         message = "Server error. Please try again later.";
// //       }
// //       else if (err?.response?.status === 400) {
// //         message = "Invalid data. Please check your inputs.";
// //       }
// //       else if (err?.response?.status === 401) {
// //         message = "Session expired. Please login again.";
// //       }
// //       else if (err?.message === "Network Error") {
// //         message = "Network issue. Check your internet connection.";
// //       }
    
// //       setApiError(message);
// //     }
    
// //     finally {
// //       setLoading(false);
// //     }
// //   };



// const handleSubmit = async (e) => {
//   e.preventDefault();

//   const validationErrors = validate();

//   if (Object.keys(validationErrors).length > 0) {
//     setErrors(validationErrors);
//     return;
//   }

//   try {
//     setLoading(true);
//     setErrors({});

//     const fd = new FormData();

//     // ✅ append all fields
//     Object.keys(form).forEach((key) => {
//       if (key !== "flow_video") {
//         fd.append(key, form[key]);
//       }
//     });

//     // ✅ append video file
//     if (form.flow_video instanceof File) {
//       fd.append("video", form.flow_video);
//     }

//     // ✅ convert values
//     fd.set("pricing", Number(form.pricing));
//     fd.set("first_day_free", Number(form.first_day_free));
//     fd.set("created_by", user.id);

//     const res = await createProjectApi(fd);

//     if (res.success) {
//       setSuccess(true);
//     }

//   } catch (err) {
//     console.log("API ERROR:", err);

//     let message = "Something went wrong. Please try again.";

//     // ✅ Backend validation errors
//     if (err?.response?.data?.errors) {
//       const errorsObj = err.response.data.errors;

//       const firstError = Object.values(errorsObj)[0];
//       message = Array.isArray(firstError) ? firstError[0] : firstError;

//       setErrors(errorsObj);
//     }

//     // ✅ Backend message
//     else if (err?.response?.data?.message) {
//       message = err.response.data.message;
//     }

//     // ✅ Friendly messages
//     else if (err?.response?.status === 500) {
//       message = "Server problem. Please try again later.";
//     } 
//     else if (err?.response?.status === 400) {
//       message = "Invalid details. Please check your input.";
//     } 
//     else if (err?.response?.status === 401) {
//       message = "Session expired. Please login again.";
//     } 
//     else if (err?.message === "Network Error") {
//       message = "No internet connection.";
//     }

//     // ✅ Show modal
//     setApiError(message);

//   } finally {
//     setLoading(false);
//   }
// };


//   const validate = () => {
//     const newErrors = {};
  
//     const requiredFields = [
//       "client", "country", "operator", "service", "flow", "pricing", 
//       "service_url", "callback_url", "server", "db_ip", 
//       "db_name", "path", "content", "content_path", "charging", "flow_video"
//     ];

//     requiredFields.forEach((field) => {
//       if (!form[field] && form[field] !== 0) {
//         // Convert to Title Case for error messages
//         const fieldName = field.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");
//         newErrors[field] = `${fieldName} is required`;
//       }
//     });
  
//     if (form.pricing && isNaN(form.pricing)) {
//       newErrors.pricing = "Pricing must be a number";
//     }
  
//     if (form.service_url && !form.service_url.startsWith("http")) {
//       newErrors.service_url = "Invalid URL";
//     }
  
//     return newErrors;
//   };

//   return (
    // <div className="min-h-screen px-6 py-6 mt-16 bg-gradient-to-br from-[#140c2b] via-[#1f1147] to-[#0f0825] md:mt-20 rounded-[20px]">

//       {/* HEADER */}
//       {/* <div className="mb-6">
//         <h1 className="text-3xl font-semibold text-white">
//           Create Project
//         </h1>
//         <p className="text-white/60 text-sm">
//           Configure your service & deployment details
//         </p>
//       </div> */}






// <div className="flex flex-col md:flex-row md:justify-between md:items-center  mb-6">

// <div>
//   <h1 className="text-3xl font-semibold text-white whitespace-nowrap">
//     Create Project
//   </h1>
//   <p className="text-white/60 text-sm">
//     Configure your service & deployment details
//   </p>
// </div>

// {/* ✅ CREATE APP BUTTON */}
// <button
//   onClick={() => navigate("/apps/create")}
//   className="px-5 py-2 bg-indigo-600 text-white rounded-lg shadow hover:bg-indigo-700 whitespace-nowrap "
// >
//   + Create App Projects
// </button>

// </div>

//       <form onSubmit={handleSubmit} className="space-y-2 grid grid-cols md:grid-cols-4 gap-5 ">

//         {/* SECTION */}
//         {/* <Section title="Basic Info"> */}
//           <Input required label="Client" value={form.client} onChange={(v)=>handleChange("client",v)}   error={errors.client}/>
//           <Input required label="Country" value={form.country} onChange={(v)=>handleChange("country",v)}  error={errors.country}/>
//           <Input required label="Operator" value={form.operator} onChange={(v)=>handleChange("operator",v)}  error={errors.operator}/>
//           <Input label="Aggregator" value={form.aggregator} onChange={(v)=>handleChange("aggregator",v)}  error={errors.aggregator}/>
//           <Input required label="Service Name" value={form.service} onChange={(v)=>handleChange("service",v)}  error={errors.service}/>
//           <Input required label="Flow" value={form.flow} onChange={(v)=>handleChange("flow",v)}  error={errors.flow}/>
//           <Input required label="Pricing" type="number" value={form.pricing} onChange={(v)=>handleChange("pricing",v)}   error={errors.pricing}/>

//           <Select
//             required
//             label="1st Day Free"
//             value={form.first_day_free}
//             onChange={(v)=>handleChange("first_day_free",v)}
//             options={[
//               { label: "No", value: 0 },
//               { label: "Yes", value: 1 },
//             ]}
//           />
      

//         {/* <Section title="URLs"> */}
//           <Input required label="Service URL" value={form.service_url} onChange={(v)=>handleChange("service_url",v)}  error={errors.service_url}/>
//           <Input label="Promotion URL" value={form.promotion_url} onChange={(v)=>handleChange("promotion_url",v)}  error={errors.promotion_url}/>
//           <Input required label="Callback URL" value={form.callback_url} onChange={(v)=>handleChange("callback_url",v)}  error={errors.callback_url}/>
//           <Input label="Header URL" value={form.header_url} onChange={(v)=>handleChange("header_url",v)}  error={errors.header_url}/>

          
//           <Input required label="Server" value={form.server} onChange={(v)=>handleChange("server",v)}  error={errors.server}/>
//           <Input required label="DB IP" value={form.db_ip} onChange={(v)=>handleChange("db_ip",v)}  error={errors.db_ip}/>
//           <Input required label="DB Name" value={form.db_name} onChange={(v)=>handleChange("db_name",v)}  error={errors.db_name}/>
//           <Input required label="Path" value={form.path} onChange={(v)=>handleChange("path",v)}  error={errors.path}/>
//         {/* </Section> */}
//         {/* </Section> */}

       



//         {/* <Section title="Content & Advanced"> */}
//           <Textarea required label="Content" value={form.content} onChange={(v)=>handleChange("content",v)}  error={errors.content}/>
//           <Input required label="Content Path" value={form.content_path} onChange={(v)=>handleChange("content_path",v)}  error={errors.content_path}/>
//           <Input required label="Charging" value={form.charging} onChange={(v)=>handleChange("charging",v)}  error={errors.charging} />
//           <Input required label="Flow Video" type="file" onChange={(v)=>handleChange("flow_video",v)} error={errors.flow_video} accept="video/*" />
//         {/* </Section> */}

//         <div className="  ">
//           <button
//             type="submit"
//             disabled={loading}
//             className="px-6 py-4 mt-6
//             rounded-xl
//               bg-gradient-to-r from-purple-600 to-indigo-600
//               text-white font-medium
//               shadow-lg hover:scale-[1.02]
//               transition 
//             "
//           >
//             {loading ? "Creating..." : "Create Project"}
//           </button>
//         </div>

//       </form>



//    {/* SUBMIT */}
//    {/* <div className="flex justify-end ">
//           <button
//             type="submit"
//             disabled={loading}
//             className="
//               px-6 py-3 mt-[-54px] rounded-xl
//               bg-gradient-to-r from-purple-600 to-indigo-600
//               text-white font-medium
//               shadow-lg hover:scale-[1.02]
//               transition 
//             "
//           >
//             {loading ? "Creating..." : "Create Project"}
//           </button>
//         </div> */}




//       {success && (
//   <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
//     <div className="bg-[#1f1147] p-6 rounded-2xl shadow-xl text-center w-[350px]">
      
//       <h2 className="text-white text-xl font-semibold mb-2">
//         🎉 Project Created
//       </h2>

//       <p className="text-white/60 text-sm mb-4">
//         Your project has been created successfully.
//       </p>

//       <button
//         // onClick={() => navigate("/projects/my")}
//         onClick={() => {
//           setSuccess(false);
//           navigate("/projects/my");
//         }}
//         className="px-5 py-2 bg-purple-600 rounded-lg text-white"
//       >
//         Go to Projects
//       </button>

//     </div>
//   </div>
// )}








// {apiError && createPortal(
//   <div className="fixed inset-0 flex items-center justify-center bg-black/50 z-50">
//     <div className=" w-[350px] rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 p-6 text-white text-center shadow-2xl">

//       <h2 className="text-red-400 text-xl font-semibold mb-2">
//         ❌ Error
//       </h2>

//       <p className="text-white/70 text-sm mb-4">
//         {apiError}
//       </p>

//       <button
//         onClick={() => setApiError("")}
//         className="px-5 py-2 bg-red-500 rounded-lg text-white"
//       >
//         Close
//       </button>

//     </div>
//   </div>,
//   document.body
// )}

//     </div>
//   );
// }

// /* ================= UI COMPONENTS ================= */

// // function Section({ title, children }) {
// //   return (
// //     <div className="
// //       bg-white/5
// //       backdrop-blur-lg
// //       border border-white/10
// //       rounded-2xl
// //       p-6
// //       shadow-[0_10px_40px_rgba(0,0,0,0.6)]
// //     ">
// //       <h2 className="text-white font-semibold mb-4 text-lg">
// //         {title}
// //       </h2>

// //       <div className="grid grid-cols-4 gap-5">
// //         {children}
// //       </div>
// //     </div>
// //   );
// // }





// function Input({ label, value, onChange, error, type = "text", required, accept }) {
//     return (
//       <div>
//         <label className="text-sm text-white/60">
//           {label} {required && <span className="text-red-500 ml-1">*</span>}
//         </label>
  
//         <input
//           type={type}
//           accept={accept}
//           value={type === "file" ? undefined : value}
//           onChange={(e) => onChange(type === "file" ? e.target.files[0] : e.target.value)}
//           className={`
//             w-full mt-1 px-4 py-3 rounded-xl
//             bg-white/5 border
//             ${error ? "border-red-500" : "border-white/10"}
//             text-white
//             focus:outline-none focus:ring-2
//             ${error ? "focus:ring-red-500" : "focus:ring-purple-500"}
//           `}
//         />
  
//         {error && (
//           <p className="text-red-400 text-xs mt-1">{error}</p>
//         )}
//       </div>
//     );
//   }





// function Textarea({ label, value, onChange, required, error }) {
//   return (
//     <div className="col-span-2">
//       <label className="text-sm text-white/60">
//         {label} {required && <span className="text-red-500 ml-1">*</span>}
//       </label>
//       <textarea
//         rows={4}
//         value={value}
//         onChange={(e) => onChange(e.target.value)}
//         className={`
//           w-full mt-1 px-4 py-3 rounded-xl
//           bg-white/5 border 
//           ${error ? "border-red-500" : "border-white/10"}
//           text-white
//           focus:outline-none focus:ring-2 
//           ${error ? "focus:ring-red-500" : "focus:ring-purple-500"}
//         `}
//       />
//       {error && (
//         <p className="text-red-400 text-xs mt-1">{error}</p>
//       )}
//     </div>
//   );
// }

// function Select({ label, value, onChange, options, required, error }) {
//   return (
//     <div>
//       <label className="text-sm text-white/60">
//         {label} {required && <span className="text-red-500 ml-1">*</span>}
//       </label>
//       <select
//         value={value}
//         onChange={(e) => onChange(e.target.value)}
//         className={`
//           w-full mt-1 px-4 py-3 rounded-xl
//           bg-white/5 border 
//           ${error ? "border-red-500" : "border-white/10"}
//           text-white
//           focus:outline-none focus:ring-2 
//           ${error ? "focus:ring-red-500" : "focus:ring-purple-500"}
//         `}
//       >
//         {options.map((o) => (
//           <option key={o.value} value={o.value} className="text-black">
//             {o.label}
//           </option>
//         ))}
//       </select>
//     </div>
//   );
// }


